import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Check, Loader2, CreditCard, Sparkles, Settings } from "lucide-react";

const statusMeta = {
  active: { label: "Active", className: "bg-emerald-100 text-emerald-700" },
  trialing: { label: "Free trial", className: "bg-blue-100 text-blue-700" },
  past_due: { label: "Payment failed", className: "bg-amber-100 text-amber-700" },
  unpaid: { label: "Unpaid", className: "bg-amber-100 text-amber-700" },
  canceled: { label: "Canceled", className: "bg-rose-100 text-rose-700" },
  incomplete: { label: "Not started", className: "bg-muted text-muted-foreground" },
};

export default function Billing() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState("");
  const [polling, setPolling] = useState(false);
  const [selectedTier, setSelectedTier] = useState("premium");

  const loadInfo = useCallback(
    () =>
      api.get("/billing").then((r) => {
        setInfo(r.data);
        const planParam = params.get("plan");
        const subscribed = ["active", "trialing"].includes(r.data.subscription_status);
        if (planParam && ["starter", "premium"].includes(planParam) && !subscribed) {
          setSelectedTier(planParam);
        } else {
          setSelectedTier(r.data.tier || "premium");
        }
        return r.data;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  // Handle Stripe Checkout return
  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        const subActive = ["trialing", "active"].includes(data.subscription_status);
        if (data.status === "complete" || subActive) {
          toast.success("Subscription started! Your free trial is active.");
          setPolling(false);
          params.delete("session_id");
          setParams(params, { replace: true });
          loadInfo();
          return;
        }
        if (data.status === "expired" || attempts > 8) {
          toast.error("Checkout was not completed.");
          setPolling(false);
          return;
        }
      } catch (e) {}
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkout = async () => {
    setBusy("checkout");
    try {
      const { data } = await api.post("/billing/checkout", {
        origin_url: window.location.origin,
        tier: selectedTier,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy("");
    }
  };

  const switchPlan = async () => {
    setBusy("switch");
    try {
      const { data } = await api.post("/billing/switch", { tier: selectedTier });
      toast.success(data.message);
      await loadInfo();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch plan");
    }
    setBusy("");
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: window.location.href.split("?")[0],
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
      setBusy("");
    }
  };

  const cancel = async () => {
    setBusy("cancel");
    const { data } = await api.post("/billing/cancel");
    toast.success(data.message);
    await loadInfo();
    setBusy("");
  };

  const resume = async () => {
    setBusy("resume");
    const { data } = await api.post("/billing/resume");
    toast.success(data.message);
    await loadInfo();
    setBusy("");
  };

  if (!info)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );

  const meta = statusMeta[info.subscription_status] || statusMeta.incomplete;
  const subscribed = ["active", "trialing"].includes(info.subscription_status);
  const canManageStripe = info.has_subscription;
  const tiers = info.tiers || [];
  const selected = tiers.find((t) => t.id === selectedTier) || tiers[0];
  const periodDate = info.current_period_end
    ? new Date(info.current_period_end).toLocaleDateString()
    : null;
  const trialDate = info.trial_ends_at ? new Date(info.trial_ends_at).toLocaleDateString() : null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/owner")}
            className="flex items-center gap-1 text-sm text-muted-foreground"
            data-testid="billing-back"
          >
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </button>
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="SmartHost" className="h-7 w-7 object-contain" />
            <span className="font-display font-bold">Billing</span>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-primary mb-1">
              Subscription
            </p>
            <h1 className="font-display text-3xl font-semibold">{info.company_name}</h1>
          </div>
          <div className="flex items-center gap-2">
            {subscribed && (
              <Badge
                className="bg-secondary text-secondary-foreground border-0 text-sm capitalize"
                data-testid="current-tier-badge"
              >
                {info.tier_name} plan
              </Badge>
            )}
            <Badge className={`${meta.className} border-0 text-sm`} data-testid="sub-status">
              {meta.label}
            </Badge>
          </div>
        </div>

        {polling && (
          <Card className="p-4 mb-6 flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <span className="text-sm">Confirming your subscription…</span>
          </Card>
        )}

        {subscribed && info.cancel_at_period_end && (
          <Card className="p-4 mb-6 bg-amber-50 border-amber-200" data-testid="cancel-notice">
            <p className="text-sm text-amber-800">
              Your subscription is set to end{periodDate ? ` on ${periodDate}` : ""}. You'll keep
              access until then.
            </p>
          </Card>
        )}

        {info.subscription_status === "past_due" && (
          <Card className="p-4 mb-6 bg-amber-50 border-amber-200" data-testid="past-due-notice">
            <p className="text-sm text-amber-800">
              Your last payment failed. Update your card in the billing portal to keep your account
              active.
            </p>
          </Card>
        )}

        {/* Tier selection */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {tiers.map((t) => {
            const isSel = selectedTier === t.id;
            const isCurrent = info.tier === t.id && subscribed;
            return (
              <button
                key={t.id}
                onClick={() => setSelectedTier(t.id)}
                data-testid={`tier-card-${t.id}`}
                className={`text-left rounded-xl border-2 p-6 transition-all ${
                  isSel
                    ? "border-primary bg-secondary/40 shadow-sm"
                    : "border-border bg-card hover:border-primary/40"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-display text-lg font-semibold flex items-center gap-2">
                    {t.name}
                    {t.id === "premium" && <Sparkles className="h-4 w-4 text-primary" />}
                  </span>
                  {isCurrent && (
                    <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[11px]">
                      Current
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mb-3">{t.tagline}</p>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="font-display text-3xl font-semibold">${t.base_fee}</span>
                  <span className="text-sm text-muted-foreground">/mo</span>
                </div>
                <ul className="space-y-1.5">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        {/* Selected plan summary */}
        <Card className="p-6 mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div>
              <p className="font-display text-3xl font-semibold capitalize">{selected?.name}</p>
              <p className="text-xs text-muted-foreground mt-1">Selected plan</p>
            </div>
            <div>
              <p className="font-display text-3xl font-semibold">{info.property_count}</p>
              <p className="text-xs text-muted-foreground mt-1">Properties</p>
            </div>
            <div>
              <p className="font-display text-3xl font-semibold">
                {Math.max(0, info.property_count - info.included_properties)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Extra × ${info.per_property_fee}</p>
            </div>
            <div className="rounded-lg bg-primary text-primary-foreground p-2">
              <p className="font-display text-3xl font-semibold">${selected?.monthly_amount}</p>
              <p className="text-xs opacity-80 mt-1">Billed / mo</p>
            </div>
          </div>
          {subscribed && info.subscription_status === "trialing" && trialDate && (
            <p className="text-sm text-muted-foreground mt-4 text-center" data-testid="trial-note">
              Free trial ends {trialDate} — you'll then be billed automatically each month.
            </p>
          )}
          {subscribed && info.subscription_status === "active" && periodDate && (
            <p className="text-sm text-muted-foreground mt-4 text-center" data-testid="renew-note">
              {info.cancel_at_period_end ? "Access ends" : "Renews automatically"} on {periodDate}.
            </p>
          )}
        </Card>

        {/* Actions */}
        <div className="space-y-3">
          {!subscribed && (
            <Button
              className="w-full h-12 text-base"
              onClick={checkout}
              disabled={busy === "checkout"}
              data-testid="start-trial-button"
            >
              {busy === "checkout" ? (
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              ) : (
                <CreditCard className="h-5 w-5 mr-2" />
              )}
              Start 3-day free trial — {selected?.name} (${selected?.monthly_amount}/mo after)
            </Button>
          )}

          {subscribed && info.tier !== selectedTier && (
            <Button
              className="w-full h-12 text-base"
              onClick={switchPlan}
              disabled={busy === "switch"}
              data-testid="switch-plan-button"
            >
              {busy === "switch" && <Loader2 className="h-5 w-5 mr-2 animate-spin" />}
              Switch to {selected?.name} (${selected?.monthly_amount}/mo)
            </Button>
          )}

          {subscribed && canManageStripe && (
            <Button
              variant="outline"
              className="w-full h-12 text-base"
              onClick={openPortal}
              disabled={busy === "portal"}
              data-testid="manage-billing-button"
            >
              {busy === "portal" ? (
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              ) : (
                <Settings className="h-5 w-5 mr-2" />
              )}
              Manage payment method & invoices
            </Button>
          )}

          {subscribed && info.cancel_at_period_end && canManageStripe && (
            <Button
              variant="outline"
              className="w-full"
              onClick={resume}
              disabled={busy === "resume"}
              data-testid="resume-sub-button"
            >
              Resume subscription
            </Button>
          )}

          {subscribed && !info.cancel_at_period_end && (
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={cancel}
              disabled={busy === "cancel"}
              data-testid="cancel-sub-button"
            >
              Cancel subscription
            </Button>
          )}
        </div>

        <p className="text-xs text-muted-foreground text-center mt-6">
          All plans include 3 properties. Additional properties: ${info.per_property_fee}/month each
          and are added to your subscription automatically. Cancel anytime — billing is handled
          securely by Stripe.
        </p>
      </div>
    </div>
  );
}
